import React from "react";
import ReactDOM from "react-dom";
import { Link } from "react-router-dom";
import { PublicLayout, usePublicData, mediaUrl, isVideoUrl } from "@/pages/public/PublicLayout";
import { InquireModal, useInquire } from "@/pages/public/InquireModal";
import { useBodyLock } from "@/pages/public/useBodyLock";

const ASSET = "/2high-assets";
const fallbackImg = (i) => {
  const pool = [
    "/assets/img/rainbow-lights-md.webp",
    "/assets/img/galactic-gas-md.webp",
    "/assets/img/moon-fuel-md.webp",
    "/assets/img/astro-runtz-md.webp",
    "/assets/img/moon-mix-md.webp",
    "/assets/img/explorer-pack-md.webp",
    "/assets/img/curated-oz-md.webp",
    "/assets/img/matte-pouch-md.webp",
  ];
  return `${ASSET}${pool[i % pool.length]}`;
};

/* Reusable empty-state visuals — no stock photos when the operator hasn't uploaded yet.
   Keeps the cosmic aesthetic but invites the operator to add real media. */
const EMPTY_TILE_BG = "radial-gradient(circle at 50% 35%, rgba(201,168,107,.14), rgba(20,18,11,.95) 70%)";
function EmptyTile({ label }) {
  return (
    <div
      aria-label={label || "Image placeholder"}
      style={{
        width: "100%",
        height: "100%",
        background: EMPTY_TILE_BG,
        border: "1px dashed rgba(201,168,107,.32)",
        display: "grid",
        placeItems: "center",
        color: "#c9a86b",
        textAlign: "center",
        opacity: .6,
      }}
    >
      <div>
        <div style={{ fontSize: 36, fontWeight: 300, lineHeight: 1 }}>+</div>
        {label && <div style={{ fontSize: 10, letterSpacing: ".2em", textTransform: "uppercase", marginTop: 8 }}>{label}</div>}
      </div>
    </div>
  );
}

export default function PublicHome() {
  return (
    <PublicLayout>
      <HomeBody />
      <InquireModal />
    </PublicLayout>
  );
}

function imgOf(p, i) {
  const raw = p.img || (p.img_set && (p.img_set.md || p.img_set.lg || p.img_set.sm)) || "";
  if (raw) return mediaUrl(raw);
  return fallbackImg(i);
}

function HomeBody() {
  const { content, products, testimonials, landed } = usePublicData();
  const inquire = useInquire();
  const c = content || {};

  // Proof detail modal — click any testimonial / landed card to open it.
  const [proofModal, setProofModal] = React.useState(null);

  // Top shelf: auto-curated as "newest 2 wholesale + newest 2 breakdown".
  // This means every new drop the operator adds in those two categories
  // automatically rotates onto the homepage — no manual starring required.
  // Empty slots (when a category has <2 products) render as premium
  // "[Drop 0X] · Coming soon" placeholders, keeping the grid balanced.
  const topShelf = React.useMemo(() => {
    const all = (products || []).filter(p => p.published !== false && p.deleted_at == null);
    const newestIn = (cat) => all
      .filter(p => (p.category || "").toLowerCase() === cat)
      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
      .slice(0, 2);
    return [...newestIn("wholesale"), ...newestIn("breakdown")];
  }, [products]);

  const dropSlots = React.useMemo(() => {
    const slots = topShelf.slice(0, 4);
    while (slots.length < 4) slots.push(null);
    return slots;
  }, [topShelf]);

  // Newest-first sorter — keeps the homepage feeling live. Anything the
  // operator posts in the Owner Cabinet bubbles to the front of every rail.
  // Falls back to `sort` field (manual order) when `created_at` is missing.
  const byNewest = React.useCallback((a, b) => {
    const ac = a.created_at || "";
    const bc = b.created_at || "";
    if (ac && bc && ac !== bc) return bc.localeCompare(ac);
    return (a.sort || 0) - (b.sort || 0);
  }, []);

  // Trust rail: customer reviews + telegram screenshots — newest first.
  const trust = React.useMemo(() => {
    return (testimonials || [])
      .filter(t => {
        const k = (t.kind || "").toLowerCase();
        return k.includes("review") || k === "telegram_screenshot";
      })
      .slice()
      .sort(byNewest)
      .slice(0, 12);
  }, [testimonials, byNewest]);

  // Landed: explicit landed collection OR fallback to testimonials.kind=landed_photo
  // Sorted newest-first so freshly-posted packages lead the rail.
  const landedList = React.useMemo(() => {
    if (Array.isArray(landed) && landed.length) {
      return landed.slice().sort(byNewest).slice(0, 12);
    }
    return (testimonials || [])
      .filter(t => (t.kind || "") === "landed_photo")
      .slice()
      .sort(byNewest)
      .slice(0, 12);
  }, [landed, testimonials, byNewest]);

  return (
    <>
      {/* HERO 11 */}
      <section className="hero11" id="hero" data-testid="hero-section">
        <div data-stagger="120" data-stagger-cap="6">
          <div className="eye rv">{c.hero_eye || "— PRIVATE COLLECTION · EXCLUSIVE COLLECTION"}</div>
          <h1 className="rv">
            <span>{c.hero_line1 || "Premium"}</span>
            <span>{c.hero_line2 || "Indoor"}</span>
            <span>{c.hero_line3 || "Exotics"}</span>
          </h1>
          <p className="sub rv">{c.hero_sub || "WELCOME TO THE TOO HIGH DIMENSION"}</p>
          <div className="cta-row rv">
            <Link className="btn solid" to="/menu#flower" data-testid="hero-menu-btn" data-magnetic="0.18">{c.hero_cta_menu || "Open Menu"}</Link>
            <Link className="btn" to="/menu#flower" data-testid="hero-build-btn" data-magnetic="0.18">{c.hero_cta_flight || "Build a Flight"}</Link>
            <Link className="btn ghost" to="/menu#wholesale" data-testid="hero-wholesale-btn" data-magnetic="0.18">{c.hero_cta_wholesale || "Wholesale Access"}</Link>
          </div>
        </div>
        <div className="hero-stage">
          <div className="hero-product">
            <div className="halo" />
            <div className="ring" />
            <div className="ring r2" />
            <div className="ring r3" />
            {c.hero_jar_img ? (
              <img
                src={mediaUrl(c.hero_jar_img)}
                alt="2High Signature Jar"
                loading="eager"
                fetchPriority="high"
              />
            ) : (
              <div
                aria-label="Hero product image placeholder"
                style={{
                  width: "min(58vmin, 520px)",
                  height: "min(58vmin, 520px)",
                  borderRadius: "50%",
                  background: "radial-gradient(circle at 35% 30%, rgba(201,168,107,.18), rgba(20,18,11,.95) 70%)",
                  border: "1px dashed rgba(201,168,107,.35)",
                  display: "grid",
                  placeItems: "center",
                  position: "relative",
                  zIndex: 2,
                }}
              >
                <div style={{ textAlign: "center", color: "#c9a86b", opacity: .55 }}>
                  <div style={{ fontSize: 56, fontWeight: 300, lineHeight: 1 }}>+</div>
                  <div style={{ fontSize: 11, letterSpacing: ".22em", textTransform: "uppercase", marginTop: 12 }}>Hero Product Image</div>
                  <div style={{ fontSize: 10, color: "#6e6f5f", marginTop: 4 }}>Upload via Owner Cabinet</div>
                </div>
              </div>
            )}
            {c.hero_jar_second && (
              <div
                className="second"
                style={{
                  backgroundImage: `url('${mediaUrl(c.hero_jar_second)}')`,
                }}
              />
            )}
            <div className="float-tag t1">{c.hero_tag1 || "Batch · 2H-JAR-001"}</div>
            <div className="float-tag t2">{c.hero_tag2 || "Indoor · Lab Tested"}</div>
          </div>
        </div>
      </section>

      {/* TRUST / PROOF */}
      {c.show_trust_section !== false && trust.length > 0 && (
        <section className="trust" id="trust" data-testid="trust-section">
          <h3 className="rv in">
            <span>{c.trust_h || "Real customers."}</span> <em>{c.trust_h_em || "Landed safely."}</em>
          </h3>
          <div className="sub rv in">{c.trust_sub || "— LIVE PROOF · UNEDITED · CLIPS FROM THE LAST 7 DAYS —"}</div>
          <div className="t-rail" data-testid="trust-rail">
            {trust.map((t, i) => {
              const src = mediaUrl(t.img);
              const video = isVideoUrl(t.img);
              // For videos: only use the real poster if it exists — NEVER fall
              // back to a random stock image (was a confusing bug where a
              // member video card showed an unrelated 2High product photo).
              // The <video preload="metadata"> below will reveal the actual
              // first frame whenever the poster isn't ready yet.
              const poster = video
                ? (t.video_poster ? mediaUrl(t.video_poster) : "")
                : (src || fallbackImg(i));
              // Strip out legacy placeholder values like "[Member]" / "[City]"
              const cleanName = cleanLabel(t.name) || cleanLabel(t.title);
              const cleanRegion = cleanLabel(t.region);
              const cleanCaption = cleanLabel(t.caption) || cleanLabel(t.quote);
              return (
                <button
                  type="button"
                  className="t-card rv in"
                  key={t.id || i}
                  data-testid={`trust-item-${i}`}
                  onClick={() => setProofModal({
                    img: t.img,
                    video,
                    poster: t.video_poster ? mediaUrl(t.video_poster) : "",
                    name: cleanName,
                    region: cleanRegion,
                    caption: cleanCaption,
                    title: cleanLabel(t.title),
                    badge: "VERIFIED MEMBER",
                  })}
                  aria-label={`Open ${cleanName || "member"} testimonial`}
                  style={{
                    appearance: "none",
                    border: 0,
                    padding: 0,
                    background: "transparent",
                    cursor: "pointer",
                    textAlign: "left",
                    color: "inherit",
                    font: "inherit",
                  }}
                >
                  <div className="ph" style={{ backgroundImage: poster ? `url('${poster}')` : "none", backgroundColor: video ? "#050714" : undefined }}>
                    {video && src && (
                      <video src={src} poster={poster || undefined} muted playsInline preload="none"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    )}
                    {!video && !t.img && (
                      // For non-video cards with no image, show a subtle gradient
                      // empty state — preserves cosmic aesthetic without using
                      // a misleading stock product photo.
                      <div style={{ width: "100%", height: "100%", background: "radial-gradient(circle at 50% 35%, rgba(201,168,107,.14), rgba(20,18,11,.95) 70%)" }} />
                    )}
                    {video && (
                      <span aria-hidden="true" style={{
                        position: "absolute", inset: 0,
                        display: "grid", placeItems: "center",
                        pointerEvents: "none",
                      }}>
                        <span style={{
                          width: 56, height: 56, borderRadius: "50%",
                          background: "rgba(0,0,0,.7)",
                          border: "1.5px solid var(--accent, #c9a86b)",
                          display: "grid", placeItems: "center",
                          color: "var(--accent, #c9a86b)",
                          fontSize: 18,
                          boxShadow: "0 8px 24px rgba(0,0,0,.55), 0 0 30px rgba(201,168,107,.35)",
                        }}>▶</span>
                      </span>
                    )}
                  </div>
                  <div className="vbadge">VERIFIED</div>
                  <div className="meta">
                    <b>{cleanName || "Member"}</b>
                    <span>{cleanCaption || cleanRegion || (video ? "Tap to play" : "Tap to view")}</span>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="label-marquee rv in">
            <div className="label-track">
              <span>{c.trust_marquee || "★ Verified Drop · Indoor Only · Batch Traceable · Hand-cured · Members Verified · 30-Day Return ·"}</span>
              <span>{c.trust_marquee || "★ Verified Drop · Indoor Only · Batch Traceable · Hand-cured · Members Verified · 30-Day Return ·"}</span>
            </div>
          </div>
        </section>
      )}

      {/* TOP-SHELF DROPS */}
      <section className="sect" id="gallery" data-testid="gallery-section">
        <div className="head-row">
          <div className="s11-head">
            <div className="eye">{c.drops_eye || "— THIS WEEK · TOP SHELF"}</div>
            <h2>
              <span>{c.drops_h || "This"}</span> <em>{c.drops_h_em || "week's"}</em> <span>{c.drops_h2 || "drops"}</span>
            </h2>
            <p className="lead">{c.drops_lead || "Four hand-selected indoor strains, freshly cured. Preview here — full menu lives on the menu page."}</p>
          </div>
          <Link className="btn rv in" to="/menu#flower" data-testid="gallery-full-menu-btn">{c.drops_cta || "View Full Menu →"}</Link>
        </div>
        <div className="tsgrid" data-testid="top-shelf-grid">
          {dropSlots.map((p, i) => {
            if (!p) {
              // Empty premium-styled placeholder — invitation to feature a product
              return (
                <Link
                  className="tscard rv in tscard-empty"
                  to="/menu"
                  key={`empty-${i}`}
                  data-testid={`top-shelf-drop-0${i + 1}`}
                  style={{ opacity: 0.55, borderStyle: "dashed" }}
                >
                  <span className="bdg live b1" style={{ background: "rgba(201,168,107,.12)", color: "#c9a86b", border: "1px solid rgba(201,168,107,.4)" }}>COMING SOON</span>
                  <div className="img" style={{ background: "linear-gradient(135deg,#0b0d14 0%,#13161f 100%)", display: "grid", placeItems: "center" }}>
                    <span style={{ fontSize: 38, color: "#c9a86b", opacity: 0.4, fontWeight: 300 }}>+</span>
                  </div>
                  <h3>[Drop <em>0{i + 1}]</em></h3>
                  <div className="meta">Reserve this slot — featured drop</div>
                </Link>
              );
            }
            const badge = (p.badge || (p.tags && p.tags[0]) || "NEW DROP").toString().toUpperCase();
            const lowish = (p.availability || "").toLowerCase().includes("limit");
            return (
              <Link className="tscard rv in" to="/menu" key={p.id || i} data-testid={`top-shelf-drop-0${i + 1}`}>
                <span className={`bdg ${lowish ? "low" : "live"} b1`}>{lowish ? "LOW STOCK" : badge}</span>
                <div className="img" style={{ backgroundImage: `url('${imgOf(p, i)}')` }} />
                <h3>{p.name || `[Drop 0${i + 1}]`}</h3>
                <div className="meta">{p.section_label || "Indoor Flower"} · {p.notes || "[Flavor notes]"}</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* BUILD A FLIGHT */}
      <section className="sect" id="flight" data-testid="flight-section">
        <div className="s11-head">
          <div className="eye">{c.flight_eye || "— BUILD YOUR FLIGHT"}</div>
          <h2>
            <span>{c.flight_h || "Mix it."}</span> <em>{c.flight_h_em || "Match it."}</em><br />
            <span>{c.flight_h2 || "Hand-built for you."}</span>
          </h2>
          <p className="lead">{c.flight_lead || "Pick your tier. Pick your flavors. We hand-build the box and confirm by Telegram. One flow. No checkout maze."}</p>
        </div>
        <div className="t11-grid rv in">
          <Link className="t11" to="/menu#breakdown" data-testid="tier-quad">
            <div className="num">{c.flight_quad_label || "— TIER 01 · STARTER"}</div>
            <h4>{c.flight_quad_name || "The Quad"}</h4>
            <div className="meta">{c.flight_quad_meta || "4 strains · eighth each"}</div>
            <p className="desc">{c.flight_quad_desc || "Pick any four strains from this week's shelf. The right size to taste before committing."}</p>
            <span className="cta-line">Start Building →</span>
          </Link>
          <Link className="t11" to="/menu#breakdown" data-testid="tier-ounce">
            <div className="num">{c.flight_ounce_label || "— TIER 02 · CURATED"}</div>
            <h4>{c.flight_ounce_name || "The Ounce"}</h4>
            <div className="meta">{c.flight_ounce_meta || "1 ounce · mix or single"}</div>
            <p className="desc">{c.flight_ounce_desc || "A full ounce — hand-built your way or curated by us. Signature jar included on every drop."}</p>
            <span className="cta-line">Start Building →</span>
          </Link>
          <Link className="t11" to="/menu#breakdown" data-testid="tier-qp">
            <div className="num">{c.flight_qp_label || "— TIER 03 · BULK"}</div>
            <h4>{c.flight_qp_name || "The QP+"}</h4>
            <div className="meta">{c.flight_qp_meta || "QP · HP · LB · operators only"}</div>
            <p className="desc">{c.flight_qp_desc || "Breakdown path. Quarter pound and up — private pricing, batch consistency, white-glove drop."}</p>
            <span className="cta-line">{c.flight_qp_cta || "Breakdowns Hub →"}</span>
          </Link>
        </div>
      </section>

      {/* WHOLESALE */}
      {c.show_wholesale_section !== false && (
        <section className="sect wholesale-visual" id="wholesale" data-testid="wholesale-section">
          <div className="s11-head">
            <div className="eye">{c.wholesale_eye || "— QUIET WHOLESALE"}</div>
            <h2>
              <span>{c.wholesale_h || "For the"}</span> <em>{c.wholesale_h_em || "operators"}</em><br />
              <span>{c.wholesale_h2 || "moving units."}</span>
            </h2>
            <p className="lead">{c.wholesale_lead || "Private pricing. Batch-matched consistency. Recurring buyer support. Quiet fulfillment."}</p>
          </div>
          <div className="wh-grid rv in">
            <Link className="wh-card" to="/menu#wholesale" data-testid="wh-qp">
              {c.wholesale_qp_img
                ? <div className="wh-img" style={{ backgroundImage: `url('${mediaUrl(c.wholesale_qp_img)}')` }} />
                : <div className="wh-img" style={{ padding: 0 }}><EmptyTile label="Wholesale · QP" /></div>}
              <div className="wh-body">
                <div className="wh-num">{c.wholesale_qp_label || "— BULK 01 · QP"}</div>
                <h4>{c.wholesale_qp_name || "QP Assortment"}</h4>
                <div className="wh-meta">{c.wholesale_qp_meta || "Quarter pound · 4 oz"}</div>
                <p className="wh-desc">{c.wholesale_qp_desc || "Single strain or curated mix. Lead time 48–72h. Recurring tier available."}</p>
                <span className="wh-cta">View Wholesale Menu →</span>
              </div>
            </Link>
            <Link className="wh-card" to="/menu#wholesale" data-testid="wh-hp">
              {c.wholesale_hp_img
                ? <div className="wh-img" style={{ backgroundImage: `url('${mediaUrl(c.wholesale_hp_img)}')` }} />
                : <div className="wh-img" style={{ padding: 0 }}><EmptyTile label="Wholesale · HP" /></div>}
              <div className="wh-body">
                <div className="wh-num">{c.wholesale_hp_label || "— BULK 02 · HP"}</div>
                <h4>{c.wholesale_hp_name || "HP Assortment"}</h4>
                <div className="wh-meta">{c.wholesale_hp_meta || "Half pound · 8 oz"}</div>
                <p className="wh-desc">{c.wholesale_hp_desc || "Variety drops with batch-matched consistency. Lead time 72h. Discreet packaging."}</p>
                <span className="wh-cta">View Wholesale Menu →</span>
              </div>
            </Link>
            <Link className="wh-card" to="/menu#wholesale" data-testid="wh-lb">
              {c.wholesale_lb_img
                ? <div className="wh-img" style={{ backgroundImage: `url('${mediaUrl(c.wholesale_lb_img)}')` }} />
                : <div className="wh-img" style={{ padding: 0 }}><EmptyTile label="Wholesale · LB" /></div>}
              <div className="wh-body">
                <div className="wh-num">{c.wholesale_lb_label || "— BULK 03 · LB"}</div>
                <h4>{c.wholesale_lb_name || "LB Assortment"}</h4>
                <div className="wh-meta">{c.wholesale_lb_meta || "Pound · single or mixed"}</div>
                <p className="wh-desc">{c.wholesale_lb_desc || "Full pound for high-volume operators. Lead time 5–7 days. Dedicated batch."}</p>
                <span className="wh-cta">View Wholesale Menu →</span>
              </div>
            </Link>
          </div>
          <div style={{ marginTop: 36 }}>
            <Link className="btn solid" to="/menu#wholesale" data-testid="wh-main-cta">{c.wholesale_cta || "View Wholesale Menu →"}</Link>
          </div>
        </section>
      )}

      {/* PROMO + HOW TO ORDER (was: founder/owner) */}
      {c.show_founder_section !== false && (
        <section className="sect" id="owner" data-testid="owner-section">
          <div className="s11-head">
            <div className="eye">{c.founder_eye || "— WATCH · VERIFY · ORDER"}</div>
            <h2>
              <span>{c.founder_h || "Three steps."}</span> <em>{c.founder_h_em || "One private line."}</em><br />
              <span>{c.founder_h2 || "Built for serious members."}</span>
            </h2>
          </div>
          <div className="owner rv in">
            <FounderVideo content={c} />
            <div className="owner-text">
              <div className="steps">
                <div className="step">
                  <div className="stp-num">1</div>
                  <div><b>{c.step1_title || "Verify · 21+"}</b><p>{c.step1_text || "Confirm your age on the door. Then DM @official2high on Telegram so we know you're real."}</p></div>
                </div>
                <div className="step">
                  <div className="stp-num">2</div>
                  <div><b>{c.step2_title || "Browse the menu"}</b><p>{c.step2_text || "Open the menu, tap a drop, save your favorites. Breakdowns for sampling · Wholesale for operators."}</p></div>
                </div>
                <div className="step">
                  <div className="stp-num">3</div>
                  <div><b>{c.step3_title || "Order on Telegram"}</b><p>{c.step3_text || "Send us your list. We confirm, hand-build the order, and drop it quietly. No accounts. No spam."}</p></div>
                </div>
              </div>
              <div className="contact11" data-testid="contact-strip">
                {c.contact_telegram_url && (
                  <a href={c.contact_telegram_url} target="_blank" rel="noreferrer" data-testid="contact-tg">
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M21.94 4.42L18.85 19.5c-.23 1.04-.85 1.29-1.72.8L12 16.6l-2.45 2.36c-.27.27-.5.5-1.02.5l.36-5.18 9.42-8.51c.41-.36-.09-.56-.63-.2L6.5 13.16 1.5 11.6c-1.09-.34-1.11-1.09.23-1.62L20.43 2.8c.91-.34 1.7.21 1.51 1.62z"/></svg>
                    <div><b>Telegram</b>{c.contact_telegram_handle || "@official2high"}</div>
                  </a>
                )}
                {c.contact_instagram_url && (
                  <a href={c.contact_instagram_url} target="_blank" rel="noreferrer" data-testid="contact-ig">
                    <svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor"/></svg>
                    <div><b>Instagram</b>{c.contact_instagram_handle || "@2high"}</div>
                  </a>
                )}
                {c.contact_tiktok_url && (
                  <a href={c.contact_tiktok_url} target="_blank" rel="noreferrer" data-testid="contact-tt">
                    <svg viewBox="0 0 24 24"><path d="M9 4v12.5a3.5 3.5 0 1 1-3.5-3.5"/><path d="M15 4c.5 3 2.5 4.5 5 4.5"/><path d="M15 4v8.5"/></svg>
                    <div><b>TikTok</b>{c.contact_tiktok_handle || "@2high"}</div>
                  </a>
                )}
                <a onClick={() => fireInquire()} data-testid="contact-inq" style={{ cursor: "pointer" }}>
                  <svg viewBox="0 0 24 24"><path d="M3 5h18v14H3z"/><path d="M3 7l9 6 9-6"/></svg>
                  <div><b>Text · Inquiry</b>Open private line</div>
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* LANDED RAIL */}
      {c.show_landed_section !== false && landedList.length > 0 && (
        <section className="landed" id="landed" data-testid="landed-section">
          <div className="landed-head">
            <h4 className="rv in">
              <span>{c.landed_h || "Packages,"}</span> <em>{c.landed_h_em || "landed."}</em>
            </h4>
            <div className="dept rv in" style={{ margin: 0 }}>{c.landed_eye || "— LIVE FEED · LAST 48 HOURS"}</div>
          </div>
          <div className="landed-rail" data-testid="landed-rail">
            {landedList.map((l, i) => {
              const src = mediaUrl(l.img);
              const video = isVideoUrl(l.img);
              // Videos: only use real poster; never fall back to stock images.
              const poster = video
                ? (l.video_poster ? mediaUrl(l.video_poster) : "")
                : (src || fallbackImg(i));
              const cleanCity = cleanLabel(l.city) || cleanLabel(l.region) || cleanLabel(l.title);
              const cleanCaption = cleanLabel(l.caption) || cleanLabel(l.quote);
              const cleanName = cleanLabel(l.name);
              return (
                <button
                  type="button"
                  className="lc rv in"
                  key={l.id || i}
                  data-testid={`landed-${i}`}
                  onClick={() => setProofModal({
                    img: l.img,
                    video,
                    poster: l.video_poster ? mediaUrl(l.video_poster) : "",
                    name: cleanName || cleanCity,
                    region: cleanName ? cleanCity : "",
                    caption: cleanCaption,
                    title: "",
                    badge: "PACKAGE · LANDED",
                  })}
                  style={{
                    appearance: "none",
                    border: 0,
                    padding: 0,
                    background: "transparent",
                    cursor: "pointer",
                    textAlign: "left",
                    color: "inherit",
                    font: "inherit",
                  }}
                  aria-label={`Open landed package${cleanCity ? ` from ${cleanCity}` : ""}`}
                >
                  <div className="ph" style={{ backgroundImage: poster ? `url('${poster}')` : "none", backgroundColor: video ? "#050714" : undefined }}>
                    {video && src && (
                      <video src={src} poster={poster || undefined} muted playsInline preload="none"
                        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                    )}
                    {video && (
                      <span aria-hidden="true" style={{
                        position: "absolute", inset: 0,
                        display: "grid", placeItems: "center",
                        pointerEvents: "none",
                      }}>
                        <span style={{
                          width: 48, height: 48, borderRadius: "50%",
                          background: "rgba(0,0,0,.7)",
                          border: "1.5px solid var(--accent, #c9a86b)",
                          display: "grid", placeItems: "center",
                          color: "var(--accent, #c9a86b)",
                          fontSize: 16,
                          boxShadow: "0 8px 24px rgba(0,0,0,.55)",
                        }}>▶</span>
                      </span>
                    )}
                  </div>
                  <div className="stamp">LANDED</div>
                  <div className="lbl">{cleanCity || `City 0${i + 1}`}</div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* CLOSING */}
      <section className="close11" id="close" data-testid="close-section">
        <div className="eye rv in">{c.closing_eye || "— FINAL CALL · MEMBERS ACCESS"}</div>
        <h2 className="rv in">
          <span>{c.closing_h || "Ready to get on the"}</span><br />
          <em>{c.closing_h_em || "private shelf?"}</em>
        </h2>
        <p className="rv in">{c.closing_p || "Three ways in. Verify your age, pick your lane, and we'll confirm everything quietly by Telegram."}</p>
        <div className="ctas rv in">
          {c.contact_telegram_url && (
            <a className="btn solid" href={c.contact_telegram_url} target="_blank" rel="noreferrer" data-testid="close-tg-btn">
              {c.closing_cta_tg || "Open Telegram"}
            </a>
          )}
          <Link className="btn" to="/menu#flower" data-testid="close-menu-btn">{c.closing_cta_menu || "Browse Full Menu"}</Link>
          <button
            className="btn ghost"
            onClick={() => inquire.open({})}
            data-testid="close-contact-btn"
          >
            {c.closing_cta_contact || "Contact 2High"}
          </button>
        </div>
      </section>

      {/* Proof detail modal — opens when a member testimonial or landed card is clicked */}
      {proofModal && (
        <ProofDetailModal data={proofModal} onClose={() => setProofModal(null)} />
      )}
    </>
  );
}

/* ====================================================================
   cleanLabel — strips legacy placeholder strings like "[Member]",
   "[City]", "[BATCH-000]" so the public site never shows bracketed
   placeholders to real visitors.
   ==================================================================== */
function cleanLabel(v) {
  if (v === null || v === undefined) return "";
  const s = String(v).trim();
  if (!s) return "";
  // Strings of the form "[Anything]" that were stored as backend defaults
  if (/^\[[^\]]+\]$/.test(s)) return "";
  return s;
}

/* ====================================================================
   ProofDetailModal — fullscreen view for member testimonials & landed
   packages. Click any card on the public homepage to open this. Plays
   video with controls. Portaled to document.body to escape parent
   stacking contexts.
   ==================================================================== */
function ProofDetailModal({ data, onClose }) {
  const { img, video, poster, name, region, caption, title, badge } = data;
  const src = img ? mediaUrl(img) : "";

  // Ref-counted body lock (handles stacking).
  useBodyLock(true);

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Forward wheel events from the backdrop into the modal's .pdm-info.
  const onBackdropWheel = (e) => {
    const info = e.currentTarget.querySelector(".pdm-info");
    if (!info) return;
    const path = typeof e.composedPath === "function" ? e.composedPath() : [];
    if (path.includes(info)) return;
    e.preventDefault();
    info.scrollTop += e.deltaY;
  };

  return ReactDOM.createPortal((
    <div
      className="pdm-backdrop"
      data-testid="proof-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onWheel={onBackdropWheel}
    >
      <button
        className="pdm-x"
        onClick={onClose}
        data-testid="proof-modal-close"
        aria-label="Close"
      >✕</button>

      <div className="pdm-card">
        <div className="pdm-media" style={{ background: "#000", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {video && src ? (
            <video
              src={src}
              poster={poster || ""}
              controls
              autoPlay
              playsInline
              style={{ width: "100%", height: "100%", maxHeight: "100%", objectFit: "contain", background: "#000" }}
              data-testid="proof-modal-video"
            />
          ) : src ? (
            <img
              src={src}
              alt={name || title || "Member proof"}
              style={{ width: "100%", height: "100%", objectFit: "contain", background: "#000" }}
              data-testid="proof-modal-img"
            />
          ) : (
            <div className="pdm-stage" style={{ background: "#0a0e1c" }} />
          )}
        </div>

        <div className="pdm-info">
          <div className="pdm-eye" data-testid="proof-modal-badge">
            {badge || "VERIFIED MEMBER"}
          </div>
          <h2 className="pdm-title" data-testid="proof-modal-name">
            {name || title || "Member"}
          </h2>
          {region && (
            <div className="pdm-row">
              <span className="pdm-pill" data-testid="proof-modal-region">{region}</span>
            </div>
          )}
          {caption && (
            <div className="pdm-section">
              <div className="pdm-h">Their words</div>
              <p style={{ whiteSpace: "pre-line" }} data-testid="proof-modal-caption">
                &ldquo;{caption}&rdquo;
              </p>
            </div>
          )}
          {!caption && !region && (
            <p style={{ color: "rgba(232, 228, 216, 0.7)" }}>
              Real member moment captured straight from the source — unedited.
            </p>
          )}
          <div className="pdm-legal">
            Real 2High member · Verified by Telegram
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}

// Helper because JSX can't call hooks inside an <a onClick>
function fireInquire() {
  window.dispatchEvent(new CustomEvent("2high:inquire", { detail: {} }));
}

function FounderVideo({ content }) {
  const c = content || {};
  const coverRaw = c.founder_cover || "";
  const cover = coverRaw ? mediaUrl(coverRaw) : "";
  // If the configured video 404s (e.g., file was wiped on a container restart
  // before the operator re-uploaded), fall back to showing only the cover image
  // instead of a broken/looping <video> tag. This keeps the homepage clean
  // even when an upload silently disappears.
  const [videoFailed, setVideoFailed] = React.useState(false);
  React.useEffect(() => { setVideoFailed(false); }, [c.founder_video]);
  const video = (!videoFailed && c.founder_video) ? mediaUrl(c.founder_video) : "";
  const posterRaw = c.founder_video_poster || c.founder_cover || "";
  const poster = posterRaw ? mediaUrl(posterRaw) : "";
  const [fullscreen, setFullscreen] = React.useState(false);

  const openFullscreen = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (video) setFullscreen(true);
  };

  return (
    <>
      <div
        className="owner-vid"
        data-testid="owner-video"
        onClick={openFullscreen}
        role={video ? "button" : undefined}
        tabIndex={video ? 0 : -1}
        onKeyDown={(e) => { if (video && (e.key === "Enter" || e.key === " ")) openFullscreen(e); }}
        aria-label={video ? "Play promo video" : undefined}
        style={{ cursor: video ? "pointer" : "default", position: "relative", overflow: "hidden" }}
      >
        {/* Base layer: video first-frame OR poster image */}
        {video ? (
          <video
            src={video}
            poster={poster || undefined}
            muted playsInline preload="none"
            onError={() => setVideoFailed(true)}
            // Critical: ignore clicks on the video element so the parent div's
            // onClick fires when the user taps anywhere on the preview area.
            style={{
              position: "absolute", inset: 0,
              width: "100%", height: "100%", objectFit: "cover",
              pointerEvents: "none",
            }}
          />
        ) : cover ? (
          <div className="ph" style={{ backgroundImage: `url('${cover}')`, backgroundSize: "cover", backgroundPosition: "center" }} />
        ) : (
          <div className="ph" style={{ position: "absolute", inset: 0, padding: 0 }}>
            <EmptyTile label="Promo video · upload via Owner Cabinet" />
          </div>
        )}

        {/* Top layer: subtle gradient + play button + expand button */}
        {video && (
          <>
            <div
              aria-hidden="true"
              style={{
                position: "absolute", inset: 0,
                background: "linear-gradient(180deg, rgba(0,0,0,.15) 0%, rgba(0,0,0,.05) 40%, rgba(0,0,0,.55) 100%)",
                pointerEvents: "none",
                zIndex: 2,
              }}
            />
            <div
              className="play-big"
              aria-hidden="true"
              style={{
                position: "absolute",
                top: "50%", left: "50%",
                transform: "translate(-50%, -50%)",
                width: 78, height: 78, borderRadius: "50%",
                background: "linear-gradient(180deg, rgba(212,182,117,.95), rgba(160,124,57,.95))",
                color: "#1a1407",
                display: "grid", placeItems: "center",
                fontSize: 28, fontWeight: 700,
                boxShadow: "0 10px 30px rgba(0,0,0,.5), 0 0 0 8px rgba(201,168,107,.18)",
                pointerEvents: "none",
                zIndex: 3,
              }}
            >▶</div>
            <button
              type="button"
              onClick={openFullscreen}
              aria-label="Open fullscreen"
              data-testid="founder-video-expand"
              style={{
                position: "absolute", top: 14, right: 14,
                width: 36, height: 36, borderRadius: 10,
                background: "rgba(8,10,8,0.75)", color: "#c9a86b",
                border: "1px solid rgba(201,168,107,.45)",
                cursor: "pointer", fontSize: 16, fontWeight: 600,
                display: "grid", placeItems: "center",
                backdropFilter: "blur(6px)",
                zIndex: 4,
              }}
            >⛶</button>
            <div
              className="v-meta"
              style={{ zIndex: 3, position: "absolute", bottom: 14, left: 16, right: 16, pointerEvents: "none" }}
            >
              <b>{c.founder_name || "2High Promo"}</b>— <span>{c.founder_duration || "0:42"}</span> · {c.founder_title || "Promo video"}
            </div>
          </>
        )}
        {!video && (
          <div className="v-meta">
            <b>{c.founder_name || "[Brand label]"}</b>— <span>{c.founder_duration || "0:00"}</span> · {c.founder_title || "Promo video"}
          </div>
        )}
      </div>

      {/* Fullscreen lightbox */}
      {fullscreen && video && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setFullscreen(false)}
          className="founder-video-lightbox"
          data-testid="founder-video-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setFullscreen(false); }}
            aria-label="Close"
            className="founder-video-lightbox__x"
            data-testid="founder-video-close"
          >✕</button>
          <video
            src={video}
            poster={poster || undefined}
            controls autoPlay playsInline
            onClick={(e) => e.stopPropagation()}
            className="founder-video-lightbox__video"
          />
        </div>
      )}
    </>
  );
}
